import { Draggable } from "@hello-pangea/dnd";
import {
    Add as AddIcon,
    MoreVert as MoreVertIcon
} from '@mui/icons-material';
import FlipToFrontIcon from '@mui/icons-material/FlipToFront';
import FlagCircleIcon from '@mui/icons-material/FlagCircle';
import {
    alpha,
    Badge,
    Box,
    Chip,
    IconButton,
    Paper,
    TextField,
    Tooltip,
    Typography,
    styled,
    tooltipClasses
} from '@mui/material';
import React, { useEffect, useRef, useState } from "react";
import isColorDark from '../../../utils/isColorDark';
import KanbanItemList from '../KanbanItemList';

// Custom Tooltip with HTML-like styling
const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#1f2937',
        color: '#ffffff',
        maxWidth: 300,
        fontSize: '0.875rem',
        border: `1px solid ${alpha('#ffffff', 0.1)}`,
        borderRadius: '8px',
        padding: '12px 16px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        '&:before': {
            content: '""',
            position: 'absolute',
            top: '100%',
            left: '50%',
            marginLeft: '-6px',
            borderWidth: '6px',
            borderStyle: 'solid',
            borderColor: `#1f2937 transparent transparent transparent`,
        }
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: '#1f2937',
        '&:before': {
            backgroundColor: '#1f2937',
            border: `1px solid ${alpha('#ffffff', 0.1)}`,
        }
    }
}));

// Styled Tooltip Content with icon
const TooltipContent = ({ title, description, color }) => (
    <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        alignItems: 'flex-start'
    }}>
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '0.9rem',
            fontWeight: 600,
            color: '#ffffff'
        }}>
            <FlagCircleIcon sx={{ fontSize: '16px', color }} />
            {title}
        </Box>
        <Typography
            variant="body2"
            sx={{
                color: '#e5e7eb',
                fontSize: '0.8125rem',
                lineHeight: 1.5,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
            }}
        >
            {description}
        </Typography>
    </Box>
);

// Column component with enhanced styling
const KanbanColumnNew = React.memo(function Column({ column, index, kanbanId, taskTitle, displayFilters, onAddItem, onLoadMore, loadingCard, expanded, handleOpenSettingsModal, priorities, tags, assigneds }) {
    const [isAdding, setIsAdding] = useState(false);
    const [newTaskTitle, setNewTaskTitle] = useState('');
    const [isHovered, setIsHovered] = useState(false);
    const [charCount, setCharCount] = useState(0);
    const formRef = useRef(null);

    // Handle click outside of the form
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (formRef.current && !formRef.current.contains(event.target) && isAdding) {
                if (newTaskTitle.trim() && charCount <= 200) {
                    handleAddTask();
                } else {
                    setIsAdding(false);
                    setNewTaskTitle('');
                    setCharCount(0);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isAdding, newTaskTitle, charCount]);

    const handleAddTask = () => {
        if (newTaskTitle.trim() && charCount <= 200) {
            const newItem = {
                id: `task-new-${Date.now()}`,
                title: newTaskTitle,
                description: "",
                dueDate: null,
                comments: 0,
                attachments: 0,
                labels: [],
                assignedUsers: [],
                checklistItems: [],
                priority: 0,
                starred: false
            };

            if (onAddItem) {
                onAddItem(column.id, newItem);
            }

            setNewTaskTitle('');
            setCharCount(0);
            setIsAdding(false);
        }
    };

    // Handle text change to update character count
    const handleTextChange = (e) => {
        const text = e.target.value;
        setNewTaskTitle(text);
        setCharCount(text.length);
    };

    // Handle keyboard events for the text field
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && charCount <= 200) {
            e.preventDefault();
            handleAddTask();
        } else if (e.key === 'Escape') {
            setIsAdding(false);
            setNewTaskTitle('');
            setCharCount(0);
        }
    };

    const handleMenuOpen = (event) => {
        event.stopPropagation();
        handleOpenSettingsModal(column.id.replace('column-', ''));
    };

    const RenderPropsIcon = ({ hasStart = false, hasFinished = false, hasConcluded = false, ...props }) => {
        if (!hasStart && !hasFinished && !hasConcluded) return null;

        const color = hasConcluded
            ? "#00C853" : hasFinished
                ? "#FFD600" : hasStart
                    ? "#2962FF" : "#BDBDBD";

        const tooltipConfig = hasConcluded
            ? {
                title: "Tarefa Concluída",
                description: "Ao mover para esta coluna, a tarefa será marcada como concluída."
            }
            : hasFinished
                ? {
                    title: "Tarefa Finalizada",
                    description: "Ao mover para esta coluna, a tarefa será marcada com a data de finalização e entrará em processo de revisão."
                }
                : hasStart
                    ? {
                        title: "Tarefa Iniciada",
                        description: "Ao mover para esta coluna, a tarefa será marcada com a data de início."
                    }
                    : {
                        title: "Tarefa em Espera",
                        description: "Esta tarefa está aguardando para ser iniciada."
                    };

        return (
            <CustomTooltip
                title={<TooltipContent
                    title={tooltipConfig.title}
                    description={tooltipConfig.description}
                    color={color}
                />}
                placement="left"
                arrow
                enterDelay={300}
                leaveDelay={100}
            >
                <Box sx={{ display: 'flex', cursor: 'help' }}>
                    <FlagCircleIcon {...props} sx={{ color, fontSize: '25px' }} />
                </Box>
            </CustomTooltip>
        );
    };

    return (
        <Draggable draggableId={column.id} index={index}>
            {(provided, snapshot) => (
                <Badge
                    sx={{
                        '& .MuiBadge-badge': {
                            top: '10px',
                            left: '20px',
                            padding: '0px',
                            border: theme => `2px solid ${alpha(column?.color || theme.palette.primary.main, 0.9)}`,
                            backgroundColor: theme => theme.palette.mode === 'dark' ? '#161c24' : '#f9f9f9',
                            height: '28px',
                            borderRadius: '35px'
                        }
                    }}
                    badgeContent={(!column?.hasStart && !column?.hasFinished && !column?.hasConcluded) ? null : <RenderPropsIcon sx={{ padding: '0px' }} hasStart={column?.hasStart || false} hasFinished={column?.hasFinished || false} hasConcluded={column?.hasConcluded || false} />}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <Paper
                        elevation={snapshot.isDragging ? 6 : 1}
                        {...provided.draggableProps}
                        ref={provided.innerRef}
                        sx={{
                            width: 300,
                            margin: 0.75,
                            backgroundColor: theme => alpha(column?.color || theme.palette.primary.main, 0.2),
                            borderRadius: 1.5,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            height: `calc(100vh - ${expanded ? 290 : 175}px)`,
                            border: theme => `1px solid ${theme.palette.mode === 'dark'
                                ? alpha(theme.palette.divider, 0.1)
                                : theme.palette.divider}`,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                boxShadow: theme => theme.palette.mode === 'dark'
                                    ? '0 8px 16px rgba(0,0,0,0.3)'
                                    : '0 6px 12px rgba(0,0,0,0.1)'
                            },
                            transform: snapshot.isDragging ? 'rotate(1deg)' : 'none',
                        }}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        {/* Aplicando dragHandleProps diretamente ao cabeçalho da coluna para resolver o erro */}
                        <Box
                            {...provided.dragHandleProps}
                            sx={{
                                py: 1.25,
                                px: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                backgroundColor: theme => column?.color || theme.palette.primary.main,
                                borderBottom: theme => `1px solid ${alpha(theme.palette.primary.dark, 0.2)}`,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                position: 'relative',
                                cursor: 'grab',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: '2px',
                                    opacity: 0.8
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {displayFilters?.showTaskCount && <Chip
                                    variant="contained"
                                    label={column.pagination.count || 0}
                                    size="small"
                                    sx={{
                                        marginRight: '10px',
                                        height: '19px',
                                        backgroundColor: (theme) => column?.color ? isColorDark(column.color) ? '#ffffff' : '#000000' : (theme.palette.mode === 'light' ? '#000000' : '#ffffff'),
                                        color: (theme) => column?.color ? isColorDark(column.color) ? '#000000' : '#ffffff' : (theme.palette.mode === 'light' ? '#ffffff' : '#000000')
                                    }}
                                />}
                                <Typography
                                    variant="body1"
                                    fontWeight="bold"
                                    whiteSpace="pre-wrap"
                                    sx={{
                                        color: (theme) => column?.color ? isColorDark(column.color) ? '#ffffff' : '#000000' : (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                                    }}
                                >
                                    {column.title}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                {isHovered && (
                                    <Tooltip placement="top" arrow title="Mover coluna">
                                        <IconButton
                                            size="small"
                                            color="inherit"
                                            sx={{
                                                p: 0.5,
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                                }
                                            }}
                                        >
                                            <FlipToFrontIcon
                                                fontSize="small"
                                                sx={{
                                                    color: (theme) => column?.color ? isColorDark(column.color) ? '#ffffff' : '#000000' : (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                                                }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip title="Adicionar cartão">
                                    <IconButton
                                        size="small"
                                        color="inherit"
                                        onClick={() => setIsAdding(true)}
                                        sx={{
                                            p: 0.5,
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255,255,255,0.2)',
                                            }
                                        }}
                                    >
                                        <AddIcon
                                            fontSize="small"
                                            sx={{
                                                color: (theme) => column?.color ? isColorDark(column.color) ? '#ffffff' : '#000000' : (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Opções da coluna">
                                    <IconButton
                                        onClick={handleMenuOpen}
                                        size="small"
                                        color="inherit"
                                        sx={{
                                            p: 0.5,
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255,255,255,0.2)',
                                            }
                                        }}
                                    >
                                        <MoreVertIcon
                                            fontSize="small"
                                            sx={{
                                                color: (theme) => column?.color ? isColorDark(column.color) ? '#ffffff' : '#000000' : (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Formulário de adição de cartão - Aparece abaixo do cabeçalho quando ativado */}
                        {isAdding && (
                            <Box
                                ref={formRef}
                                sx={{
                                    padding: '12px 12px 0px 12px',
                                    backgroundColor: theme => theme.palette.mode === 'dark'
                                        ? alpha(column?.color || theme.palette.primary.main, 1)
                                        : alpha(column?.color || theme.palette.primary.main, 1),
                                    borderBottom: theme => `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <TextField
                                    autoFocus
                                    fullWidth
                                    multiline
                                    maxRows={3}
                                    size="small"
                                    variant="outlined"
                                    placeholder="Título do novo cartão..."
                                    value={newTaskTitle}
                                    onChange={handleTextChange}
                                    onKeyDown={handleKeyDown}
                                    error={charCount > 200}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgb(22 28 36)' : '#ffffff',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                            borderRadius: '5px',
                                            '&:hover': {
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme => charCount > 200
                                                        ? 'error.main'
                                                        : alpha(column?.color || theme.palette.primary.main, 0.3)
                                                }
                                            },
                                            '&.Mui-focused': {
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderWidth: '1px',
                                                    borderColor: theme => charCount > 200
                                                        ? 'error.main'
                                                        : alpha(column?.color || theme.palette.primary.main, 0.3)
                                                }
                                            }
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            padding: '5px 7px',
                                            fontSize: '0.85rem'
                                        }
                                    }}
                                />

                                {/* Contador de caracteres */}
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-end',
                                }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: theme => charCount > 200 ? 'error.main' : column?.color ? isColorDark(column.color) ? '#ffffff' : '#000000' : (theme.palette.mode === 'light' ? '#000000' : '#ffffff'),
                                            fontWeight: charCount > 200 ? 'bold' : 'normal'
                                        }}
                                    >
                                        {charCount}/200
                                    </Typography>
                                    {charCount > 200 && (
                                        <Typography
                                            variant="caption"
                                            color="error"
                                            sx={{ mt: 0.5 }}
                                        >
                                            O texto é muito longo. Limite de 200 caracteres.
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        )}

                        <Box sx={{
                            flex: 1,
                            overflow: 'hidden'
                        }}>
                            <KanbanItemList
                                columnId={column.id}
                                column={column}
                                onLoadMore={onLoadMore}
                                loadingCard={loadingCard}
                                priorities={priorities}
                                tags={tags}
                                assigneds={assigneds}
                                expanded={expanded}
                                displayFilters={displayFilters}
                                taskTitle={taskTitle}
                                kanbanId={kanbanId}
                            />
                        </Box>

                        {/* Rodapé vazio para preservar o layout */}
                        <Box sx={{
                            p: 1.5,
                            mt: 'auto',
                            borderTop: theme => `1px solid ${theme.palette.mode === 'dark'
                                ? alpha(column?.color || theme.palette.primary.main, 0.1)
                                : alpha(column?.color || theme.palette.primary.main, 0.1)}`,
                            backgroundColor: theme => theme.palette.mode === 'dark'
                                ? alpha(column?.color || theme.palette.primary.main, 0.6)
                                : alpha(column?.color || theme.palette.primary.main, 0.6),
                            boxShadow: '0 -1px 3px rgba(0,0,0,0.02)'
                        }} />
                    </Paper>
                </Badge>
            )}
        </Draggable>
    );
});

export default KanbanColumnNew;